// utils/supplierSuggestion.js
//
// The SINGLE reader for the shop the MEMBER named — on one of her own order
// lines (`supplierSuggestion`) or on a product she has never ordered
// (`mySuggestion`). Both attributes carry the SAME payload, from the same
// backend projection, so one reader serves both surfaces.
//
// BE→FE briefs: `2026-09-05-member-supplier-suggestion.md` (the door),
// `2026-09-09-suggestion-carries-its-answer.md` (the answer),
// `2026-09-09-product-level-suggestion-and-oos-feed.md` (the product surface).
//
// THE CONTRACT:
//
//   { id, at: ISO, supplierName: string, supplierId?: int, note?: string|null,
//     priceSeenCents?: int, status: 'open'|'accepted'|'declined'|'superseded',
//     intent: 'shop'|'keep_trying', anyPrice: bool,
//     answeredAt: ISO|null, answerNote: string|null,
//     outcome: 'worked'|'not_there'|null, outcomeAt: ISO|null }
//
// ── ROSTER ONLY, ONCE PER LINE, WITH A PRICE (2026-09-15) ────────────────────
//
// Brief `2026-09-15-roster-only-tips-once-per-line.md`, owner answers H-B/H-C.
// Both doors now take `supplier_id` (REQUIRED, from the club's roster), an
// optional `note` and an optional `price_seen_cents`. `supplier_name`, `intent`
// and `any_price` are REFUSED with a 422, and the line door answers 403 once the
// line has spent its one tip.
//
// Three consequences this module carries, and none of them is a deletion:
//
//  * `priceSeenCents` is read and rendered. Present only when she gave a price.
//  * `intent` and `anyPrice` STAY on the wire and stay read here. Twelve legacy
//    rows and the four migration-declined ones carry them, and their receipts
//    must keep rendering — a member whose tip was declined last week still opens
//    her line and reads why. Nothing NEW ever writes them; a new tip reads
//    `shop` / `false`.
//  * `supplierSuggestionUsed` is a SEPARATE attribute on the line, not part of
//    this payload. It never ages off, while an answered `supplierSuggestion`
//    drops after thirty days — so the door's gate reads that flag and the
//    receipt reads this payload, and the two are deliberately not the same
//    question.
//
// ── THE ANSWER IS STORED NOW, AND THE RECEIPT NO LONGER VANISHES ─────────────
//
// This module used to treat PRESENCE as "still waiting", because the backend
// dropped the key the moment the admin answered. That disappearance was the
// whole signal — and it was also the whole defect (gap U-40): she never read the
// answer, and never read the DECLINE REASON, because the only thing that ever
// reached her screen was «اقتراحك وصل للأدمن» followed by silence.
//
// `status` is stored server-side now and only an ANSWER closes a suggestion — a
// line moving no longer does. So:
//
//   * `open` is the ONLY "still waiting". It is the only status that shuts the
//     door, and the only one that offers "change what I sent".
//   * `accepted` / `declined` / a written `outcome` are NEWS, and they render on
//     her line for thirty days. The door re-opens underneath them: the answer is
//     history, not a reason to stop her naming a second shop.
//   * `superseded` renders NOTHING. It means nobody can act on the tip any more
//     — her line closed for good, or she replaced it — and the backend never
//     pushes it for exactly that reason: there is no news in it for her.
//
// PRESENCE IS STILL THE FALLBACK. A payload with no `status` key is an OPEN
// suggestion from a backend that predates the answer store, which is what keeps
// this readable against a deployment where the API is behind the app.
//
// ── FOUR RULES THIS MODULE ENFORCES SO NO CALLER HAS TO REMEMBER THEM ────────
//
//  1. ABSENCE IS AN OMITTED KEY, NEVER NULL — for the payload and for the
//     optional `note` inside it. Callers branch on this returning null; they
//     never inspect the raw attribute. Code that tries to tell "absent" from
//     "explicit null" is meaningless — the wire cannot express the difference.
//  2. THE SHOP NAME IS THE FACT — EXCEPT WHEN SHE NAMED NO SHOP. A `shop` tip
//     with no usable `supplierName` is not something we can show her, so it
//     returns null rather than rendering an empty "you told us to try  ". A
//     `keep_trying` tip carries an EMPTY name by construction (the backend's
//     `shopLabel()` returns '' for it) and is a complete, valid suggestion — she
//     asked us to go out again, she did not name a place. Requiring a name there
//     would silently hide the receipt for the exact members this intent exists
//     for. The DATE is secondary and may legitimately be missing (the backend
//     emits `''` when the row has no timestamp), so it degrades to null on its
//     own and the caller picks the date-less copy.
//  3. EVERY ENUM IS READ, NEVER ASSUMED. An unknown `status`, `intent` or
//     `outcome` normalises to null rather than reaching a member as a raw key —
//     the discipline `productAvailability.js` states beside this file, and the
//     one `walletKind` violated once. An unknown status therefore says nothing
//     and re-opens the door, which is the safe direction: she can still act.
//  4. ONE LABEL WRITER. `suggestionLabel()` is the only place a suggestion turns
//     into a sentence, because five surfaces render it — the order row, the item
//     details sheet, a resolved question's card, the post card and the product
//     page — and they must read as one sentence about one thing wherever she
//     meets it.
//
// Pure function module — no Vue, no F7, no i18n import. `suggestionLabel` takes
// `t` as an argument rather than reaching for the global, the same shape
// utils/transactionDisplay.js uses, so this stays testable without mounting.

import { ISSUE_STATUS } from '@/constants/collectionIssue.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import { formatAvailabilityDate } from '@/utils/productAvailability.js';

function trimmedOrNull(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Parse an ISO-8601 instant, or null. A malformed date renders NOTHING rather
 *  than "Invalid Date" — same call `productAvailability.js` makes. */
function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A positive integer id, or null. Anything else — a float, a zero, a string that
 * is not a number — is not an id we can pre-select a radio with or post as
 * `supplier_id`, and guessing would name the wrong shop.
 *
 * Exported because the roster reader applies the identical rule to the rows it
 * offers her: the id it renders and the id read back off her item have to agree
 * about what counts as an id, or a shop she picked fails to re-select.
 */
export function positiveIntOrNull(value) {
  if (value == null) return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** What the club SAID about her tip. Only an ANSWER closes a suggestion — a line
 *  moving no longer does (gap C-43). */
export const SUGGESTION_STATUS = Object.freeze({
  OPEN: 'open',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  SUPERSEDED: 'superseded',
});

/** What she MEANT when she used the door (gap U-41). A `keep_trying` tip names
 *  NO shop and carries no supplier id — that is its whole shape, not a gap. */
export const SUGGESTION_INTENT = Object.freeze({
  SHOP: 'shop',
  KEEP_TRYING: 'keep_trying',
});

/** What the world DID at the shop she named. Independent of the status: an
 *  accepted tip can still turn out `not_there`. */
export const SUGGESTION_OUTCOME = Object.freeze({
  WORKED: 'worked',
  NOT_THERE: 'not_there',
});

const STATUS_VALUES = new Set(Object.values(SUGGESTION_STATUS));
const INTENT_VALUES = new Set(Object.values(SUGGESTION_INTENT));
const OUTCOME_VALUES = new Set(Object.values(SUGGESTION_OUTCOME));

/** A known enum value, or null. Rule 3: an unknown key never reaches a member. */
function knownOrNull(value, allowed) {
  return typeof value === 'string' && allowed.has(value) ? value : null;
}

/**
 * The member's tip and the club's answer to it — or null when there is nothing
 * we can show her.
 *
 * ONE reader for BOTH attributes, because they carry byte-identical payloads
 * from the same backend projection (`ProductSupplierSuggestion::asMemberPayload()`):
 * `supplierSuggestion` on an order line she owns, `mySuggestion` on a product or
 * a post she has merely read. A member who names a shop from a post and then
 * opens her order line must read the same sentence about the same tip in both
 * places, and one reader is how that stays true.
 *
 * `supplierId` is DERIVED BACKEND-SIDE, never stored (oos-loop2 P2-A). It is
 * present only when the shop she named is EXACTLY — case- and
 * whitespace-insensitively — a name on the club's selectable roster, through the
 * same `SupplierService::idForExactName()` that pre-selects the leader's accept
 * sheet, so the sheet and her app can never name different shops for one
 * suggestion. It is OMITTED otherwise, which is both the common case and the
 * valuable one: the shop the club has never bought from is the whole reason for
 * asking her.
 *
 * Its purpose is the round trip. A shop she originally PICKED used to come back
 * as free text, because the payload reported a display string with no id behind
 * it; with the id the sheet re-selects the row she chose instead of dropping it
 * into the typed field. Because it resolves on READ, a shop renamed after she
 * named it stops reporting an id and her words stay untouched — the right way
 * round, since a stale id would re-select a row the leader's own sheet can no
 * longer offer.
 *
 * @param {object|null|undefined} resource Anything carrying the attribute: an
 *   OrderBoxItem, a Product, a Post, or any of their raw resources.
 * @param {string} [key] which attribute to read — `supplierSuggestion` on a
 *   line, `mySuggestion` on a product or post.
 * @returns {{id: string|null, at: Date|null, status: string|null,
 *   intent: string|null, anyPrice: boolean, supplierName: string|null,
 *   supplierId: number|null, note: string|null, priceSeenCents: number|null,
 *   answeredAt: Date|null,
 *   answerNote: string|null, outcome: string|null, outcomeAt: Date|null}|null}
 */
export function readSupplierSuggestion(resource, key = 'supplierSuggestion') {
  const raw = resource?.[key];
  if (raw == null || typeof raw !== 'object') return null;

  const intent = knownOrNull(raw.intent, INTENT_VALUES);
  const supplierName = trimmedOrNull(raw.supplierName);

  // Rule 2. A `keep_trying` tip names no shop BY CONSTRUCTION — the backend's
  // `shopLabel()` returns '' for it — so requiring a name here would hide the
  // receipt from precisely the members this intent was added for. Every other
  // shape (including a legacy payload with no `intent` key at all) still needs
  // one, because "you told us to try  " is not a sentence.
  if (supplierName === null && intent !== SUGGESTION_INTENT.KEEP_TRYING) return null;

  return {
    id: raw.id == null ? null : String(raw.id),
    at: toDate(raw.at),
    // PRESENCE IS THE FALLBACK: a payload with no `status` key comes from a
    // backend that predates the answer store, and every such payload is by
    // definition an open one — the old contract dropped the key on answer.
    status: 'status' in raw ? knownOrNull(raw.status, STATUS_VALUES) : SUGGESTION_STATUS.OPEN,
    intent,
    anyPrice: raw.anyPrice === true,
    supplierName,
    supplierId: positiveIntOrNull(raw.supplierId),
    note: trimmedOrNull(raw.note),
    // Piasters, omitted when she named no price. Read through the SAME positive-
    // integer rule as an id, for the same reason: a zero, a float or a string
    // that is not a number is not a price we can put in a sentence, and the
    // backend's own floor is 100 (one pound).
    priceSeenCents: positiveIntOrNull(raw.priceSeenCents),
    answeredAt: toDate(raw.answeredAt),
    answerNote: trimmedOrNull(raw.answerNote),
    outcome: knownOrNull(raw.outcome, OUTCOME_VALUES),
    outcomeAt: toDate(raw.outcomeAt),
  };
}

/**
 * Her tip on this line, ONLY while the club still owes her an answer.
 *
 * This is the "is the door shut" reader and the sheet's seed — both of which are
 * about an UNANSWERED tip and neither of which should fire on an answered one.
 * For rendering, use `readSupplierSuggestion` + `suggestionLabel`: an answered
 * tip is still news for thirty days, and it is the news she never used to get.
 *
 * @param {object|null|undefined} resource
 * @param {string} [key]
 */
export function openSupplierSuggestion(resource, key = 'supplierSuggestion') {
  const suggestion = readSupplierSuggestion(resource, key);
  return suggestion?.status === SUGGESTION_STATUS.OPEN ? suggestion : null;
}

/**
 * The one sentence for a suggestion in ANY state — the receipt while it waits,
 * and the answer once it has one.
 *
 * ONE writer for FIVE surfaces: the order row, the item details sheet, a
 * resolved question's card, the post card and the product page all render this,
 * and they must read as one sentence about one thing wherever she meets it. It
 * lived in three files as a copy of the same ternary until the third arrived;
 * the date formatter, the two-key pair and the missing-date branch are exactly
 * the things that drift when it does.
 *
 * ── THE ORDER OF PRECEDENCE, AND WHY ────────────────────────────────────────
 * What the world DID beats what the club SAID. An `outcome` is written from the
 * collection events themselves — the line was collected at her shop, or a try
 * there came back empty — so it is both the freshest and the most concrete thing
 * we can tell her, and it is the fact she can check against her own order. The
 * status is the club's word about a plan; the outcome is what happened.
 *
 * It falls back to the status when an outcome exists but names no shop, since
 * both outcome sentences are ABOUT a named shop.
 *
 * `superseded` and every unknown status return '' — nobody can act on the tip
 * any more, which is why the backend never pushes it either. Callers gate on
 * falsiness, so an empty string simply renders nothing.
 *
 * ── THE PRICE SHE SAW (2026-09-15) ──────────────────────────────────────────
 * A new tip may carry `priceSeenCents`, and the OPEN receipt says it back to
 * her: it is the one fact on the tip she could have got wrong, and reading it
 * on her own line is how she notices.
 *
 * `formatMoney` is an OPTIONAL dependency rather than an import, because this
 * module is pure by rule — no Vue, no F7, no i18n — and the app's only money
 * formatter lives inside `useCurrency()`. A caller that passes it gets the
 * price arm; one that does not gets exactly today's sentence, with the price
 * silently left out rather than rendered as raw piasters. That degradation is
 * the right way round: «45000» in a sentence about a price is worse than no
 * price at all, and every surface that renders this already holds the
 * formatter.
 *
 * Only the OPEN arms take a price. An ANSWERED tip is about what the club did —
 * a day, a reason, an outcome — and repeating what she thought it would cost
 * beside the club's answer adds nothing and can contradict it.
 *
 * @param {ReturnType<typeof readSupplierSuggestion>} suggestion
 * @param {{t: Function, locale?: string, formatMoney?: Function}} deps
 * @returns {string} '' when there is nothing to say.
 */
export function suggestionLabel(suggestion, { t, locale = 'ar', formatMoney } = {}) {
  if (!suggestion || typeof t !== 'function') return '';

  const supplier = suggestion.supplierName;

  if (supplier !== null) {
    if (suggestion.outcome === SUGGESTION_OUTCOME.WORKED) {
      return t('item.suggestSupplier.answered.worked', { supplier });
    }
    if (suggestion.outcome === SUGGESTION_OUTCOME.NOT_THERE) {
      return t('item.suggestSupplier.answered.notThere', { supplier });
    }
  }

  // The date is the SECONDARY fact and legitimately absent, so the open arms
  // pick the date-less string rather than rendering an empty fragment.
  const date = suggestion.at ? formatAvailabilityDate(suggestion.at, locale) : null;

  switch (suggestion.status) {
    case SUGGESTION_STATUS.OPEN: {
      // LEGACY, AND KEPT ON PURPOSE. Nothing writes `keep_trying` any more —
      // the intent question is gone from the sheet and the key is a 422 on both
      // doors — but the four tips the 2026-09-15 migration declined and the
      // twelve older rows still carry it, and every one of them belongs to a
      // member who can open her line today. Deleting this arm would blank their
      // receipts, which is the same defect (U-40) as never showing an answer.
      if (suggestion.intent === SUGGESTION_INTENT.KEEP_TRYING) {
        return date
          ? t('item.suggestSupplier.pending.keepTryingWithDate', { date })
          : t('item.suggestSupplier.pending.keepTryingNoDate');
      }

      // The price arm needs BOTH a price and a formatter; without either it
      // falls through to the sentence this has always written.
      const price =
        suggestion.priceSeenCents != null && typeof formatMoney === 'function'
          ? formatMoney(suggestion.priceSeenCents)
          : null;

      if (price) {
        return date
          ? t('item.suggestSupplier.pending.withPrice', { supplier, price, date })
          : t('item.suggestSupplier.pending.withPriceNoDate', { supplier, price });
      }

      return date
        ? t('item.suggestSupplier.pending.withDate', { supplier, date })
        : t('item.suggestSupplier.pending.noDate', { supplier });
    }

    case SUGGESTION_STATUS.ACCEPTED:
    // `answerNote` on an ACCEPTED row is the collection day IN WORDS, already
    // Arabic, written by the backend so her app and the leader's sheet name the
    // same day. Its ABSENCE is the second accept shape (locked answer L-G):
    // there was nothing to send — her carton had shipped, or she named the shop
    // for something she never ordered — so the shop was recorded for next time
    // and she was thanked. That branch must never imply a delivery.
      return suggestion.answerNote
        ? t('item.suggestSupplier.answered.acceptedWithDay', { day: suggestion.answerNote })
        : t('item.suggestSupplier.answered.acceptedNoDay');

    case SUGGESTION_STATUS.DECLINED:
    // THE REASON SHE HAS NEVER BEEN ABLE TO SEE (gap U-40). `answer_note` is
    // required on a decline server-side, so the reason-less arm is a genuine
    // fallback rather than the common case.
      return suggestion.answerNote
        ? t('item.suggestSupplier.answered.declined', { reason: suggestion.answerNote })
        : t('item.suggestSupplier.answered.declinedNoReason');

    default:
      return '';
  }
}

/**
 * Does this line still have a suggestion the admin has not answered?
 *
 * The door to SEND one must be closed while this is true — she has already told
 * us, and a second button beside "your suggestion reached the team" reads as
 * though the first one did not register. Correcting it is a separate, quieter
 * affordance.
 *
 * It opens again on an ANSWER, which is the change of 2026-09-09: the answer is
 * history and she may well know a second shop. The receipt stays on screen
 * underneath the re-opened door — an answer she can read, not a door she cannot.
 *
 * @param {object|null|undefined} resource
 * @param {string} [key]
 * @returns {boolean}
 */
export function hasOpenSupplierSuggestion(resource, key = 'supplierSuggestion') {
  return openSupplierSuggestion(resource, key) !== null;
}

/**
 * May the member send a suggestion for this line RIGHT NOW?
 *
 * A faithful mirror of `OrderBoxItemPolicy::suggestSupplier()` — all three arms,
 * exactly, since 2026-09-07. Rendering the door anywhere else is a guaranteed
 * 403, and a button that only ever fails is worse than no button.
 *
 * The three arms the policy allows (her own line in every case):
 *   1. status OUT_OF_STOCK — nobody could source it.
 *   2. status PENDING and NOT on a live collection run.
 *   3. a question on the line sitting at `awaiting_member`.
 *
 * ARM 2 IS NOW EXACT (oos-loop2 P2-A, brief 2026-09-07). It used to be
 * approximated — every PENDING line was offered the door and a 403 was the
 * answer — because `isInActivePlan()` had no field on `OrderBoxItemResource`.
 * The backend now emits `inActivePlan: true` when a collector is out buying the
 * line, reading the SAME predicate the write lock uses, so what this hides is
 * exactly what the policy would refuse rather than a guess at it.
 *
 * TESTED FOR PRESENCE, NOT TRUTH. The key is `true` or OMITTED, never `false`,
 * so `!== true` is the whole check: an absent key and an explicit false mean the
 * same thing here, and reading it any other way would re-introduce the "absent
 * means unknown" ambiguity the omitted-key contract exists to remove.
 *
 * Note it does NOT gate arm 1. A line already marked out of stock may sit on a
 * live plan, and naming a shop is precisely what is useful about it — the policy
 * allows that, and so does this.
 *
 * Anything past collection — COLLECTED, PACKAGED, SHIPPED, DELIVERED, CANCELLED,
 * WRITTEN_OFF — is false here and a 403 there.
 *
 * ── THE FOURTH ARM: THE CARTON HAS TO STILL TAKE IT BACK (oos-desk P3-E,
 * brief 2026-09-14-suggestion-door-closed-on-moved-on-cartons) ─────────────────
 * The three arms above are about the LINE. Since 2026-09-14 the backend also
 * refuses when the line's CARTON has moved on — packed, shipped, delivered,
 * closed, cancelled, or delivery-confirmed — even while the line itself is
 * still OUT_OF_STOCK, PENDING off a plan, or holds an `awaiting_member`
 * question: there is nowhere left for a shop's goods to arrive. This mirrors
 * `OrderBoxItem::boxCanTakeItBack()` — carton `status` ACTIVE or READY, and
 * `deliveredConfirmedAt` null — exactly, the same window the wall-eligibility
 * parity test pins on the backend side.
 *
 * `box` is OPTIONAL and backward compatible: a caller that does not pass it
 * gets exactly today's three-arm answer, unchanged. A caller that does pass
 * it — the carton itself, or `{ status, deliveredConfirmedAt }` — gets the
 * fourth arm too, so a stale screen never offers a door the backend has
 * already shut.
 *
 * ── THE FIFTH ARM: ONE TIP PER LINE, FOR ITS WHOLE LIFE (oos-hold P1-B,
 * brief 2026-09-15-roster-only-tips-once-per-line, owner answer H-B) ──────────
 * The line door now answers 403 once the line's tip has been ANSWERED, declined
 * or superseded, and `supplierSuggestionUsed` is the fact that says so. It is
 * checked FIRST, before any of the line arms, because it is the widest and the
 * cheapest: a spent line can satisfy every other arm and still be refused.
 *
 * WHY IT IS NOT `readSupplierSuggestion(...)?.status !== 'open'`. The two look
 * interchangeable and are not. An answered `supplierSuggestion` AGES OFF after
 * thirty days; `supplierSuggestionUsed` never does. Deriving the gate from the
 * payload would therefore re-open the door a month later on a line the backend
 * has permanently closed — a button that can only 403, which is exactly what
 * this arm exists to prevent.
 *
 * TESTED FOR PRESENCE, NOT TRUTH, like `inActivePlan` beside it: the key is
 * `true` or OMITTED, never `false`.
 *
 * It does NOT close the door on an OPEN tip. That one is still hers to correct —
 * a re-POST updates the same row — and `hasOpenSupplierSuggestion` is what the
 * surfaces read to swap the send button for «غيّر اقتراحك».
 *
 * @param {object|null|undefined} item an OrderBoxItem model or raw resource
 * @param {{status: string|number, deliveredConfirmedAt: string|Date|null}|undefined} [box]
 *   the item's OrderBox, or an equivalent plain shape. Omit to keep the
 *   line-only answer.
 * @returns {boolean}
 */
export function canSuggestSupplier(item, box) {
  if (item == null) return false;

  // Spent — for good, and regardless of everything below.
  if (item.supplierSuggestionUsed === true) return false;

  // Coerced the way useItemStatus coerces: a string `"41"` off the wire renders
  // a correct chip while answering false to every strict `===` gate beside it.
  const raw = item.status;
  const status = typeof raw === 'string' ? Number(raw) : raw;

  let lineCanAsk;
  if (status === ItemStatus.OUT_OF_STOCK) {
    lineCanAsk = true;
  } else if (status === ItemStatus.PENDING) {
    lineCanAsk = item.inActivePlan !== true;
  } else {
    lineCanAsk = item.activeIssue?.status === ISSUE_STATUS.AWAITING_MEMBER;
  }

  if (!lineCanAsk) return false;
  if (box === undefined) return true;

  return boxCanTakeItBack(box);
}

/**
 * The FE mirror of `OrderBoxItem::boxCanTakeItBack()` — exactly. A carton not
 * ACTIVE or READY has moved past taking the item back; one already
 * delivery-confirmed is refused even if its status lags, because re-billing a
 * delivered item is a settlement question, not a status flip. A missing box
 * answers false, same as the backend's own null-box branch.
 *
 * EXPORTED since oos-hold P3-A. It was private while the suggestion door was
 * its only reader; the member's CANCEL on an out-of-stock line now reads the
 * SAME carton fact (brief `2026-09-15-oos-line-cancel-and-the-hold.md`, owner
 * answer H-M — `OrderBoxService::memberCancelItem()` admits OUT_OF_STOCK when
 * `boxCanTakeItBack()`). Two windows that must be the same window, so there is
 * ONE definition of it rather than a second copy inside `useItemStatus`.
 *
 * @param {object|null|undefined} box
 * @returns {boolean}
 */
export function boxCanTakeItBack(box) {
  if (box == null) return false;

  const raw = box.status;
  const status = typeof raw === 'string' ? Number(raw) : raw;

  return (
    (status === OrderBoxStatus.ACTIVE || status === OrderBoxStatus.READY)
    && box.deliveredConfirmedAt == null
  );
}

/** The backend's ceiling on the optional sentence (`note` → `max:500`). Over it
 *  is a 422, so the field caps here rather than letting her write past it. */
export const SUPPLIER_SUGGESTION_NOTE_MAX = 500;

/**
 * And on a typed shop name (`supplier_name` → `max:120`).
 *
 * ⚠ CUT — pending the cleanup pass (oos-hold P3-A, owner answer H-C,
 * 2026-09-15). `supplier_name` is REFUSED with a 422 on both doors now and the
 * sheet's typed field is gone, so nothing reads this any more. Left in place
 * rather than deleted inline, per this repo's no-inline-deletes rule; the
 * backend keeps the column declared for the sixteen legacy rows on record.
 */
export const SUPPLIER_NAME_MAX = 120;

/**
 * The backend's floor on the optional price she saw (`price_seen_cents` →
 * `integer|min:100`). One hundred piasters is one pound: under it is a 422, so
 * the sheet refuses it before spending the round-trip.
 */
export const PRICE_SEEN_MIN_CENTS = 100;
